// Port of SuiteModel/DocumentFilter.swift: what each rule of a collection or the binder's filter tests (`FilterFacts`),
// the words a text rule looks for (`WordQuery`), and the tree a filtered arrangement shows (`FilterResult`). The facts
// come from the documents (`searchMembers`, exact, for compile) or from what the project view already holds
// (`liveFacts`); the rules are tested by the same code either way. `design-docs/Binder filters — design.md`.
import type { FragmentKind, MetadataValue } from '../contract.ts';
import { DISTANT_PAST, type Arrangement, type ArrangementNode, type FragmentRecord, type MetadataField, type StoryEntity } from '../model/documents.ts';
import { flattened, tree as buildTree, type ArrangementTreeNode } from '../model/arrangement.ts';
import { plainText as fountainPlainText } from '../fountain.ts';
import { mapPlainText } from '../maps/map.ts';
import { plainText, wordCount } from '../proseMarkdown.ts';
import { presenceRank, type PresenceLevel, type PresenceRow } from '../presence.ts';
import { fragmentTags, hasTag, joinTags, parseTags } from '../tags.ts';
import { graphemes, isCFWhitespaceOrNewline, scalars, trimCFWhitespace } from '../unicode.ts';
import { fieldDisplay } from './fields.ts';
import { WordTarget } from './targets.ts';
import { displayString, ruleValue, type SavedSearchRule } from './savedSearch.ts';

// MARK: Facts

/** `FilterFacts.CommentFact`: an open comment thread on the text, its category and who started it. */
export interface CommentFact {
  category: string | null;
  author: 'human' | 'agent';
}

/** `FilterFacts`: what a rule is tested against, one fragment. */
export interface FilterFacts {
  id: string;
  title: string;
  kind: FragmentKind;
  /** A folder (or a document with documents under it): "document" and "folder" in a kind rule tell them apart. */
  isFolder: boolean;
  /** Every field's stored value (a tags field as its tags joined, every device's). */
  values: Record<string, MetadataValue>;
  /** Who's in it, at their highest level (POV, on stage, mentioned), explicit or inferred. */
  presence: Map<string, PresenceLevel>;
  /** Its own words, and with everything under it (for word targets). */
  words: number;
  subtreeWords: number;
  modifiedAt: Date;
  includeInCompile: boolean;
  locked: boolean;
  synopsis: string;
  notesEmpty: boolean;
  comments: CommentFact[];
  openSuggestions: number;
  /** Its nodes and every node above them (for "within"). */
  nodes: Set<string>;
  /** Text the index otherwise holds, when the documents are read (`searchMembers`): rules on it test it directly. */
  text?: { body: string; notes: string };
}

/** `FilterFacts.init`'s defaults. */
export const filterFacts = (o: Partial<FilterFacts> & { id: string }): FilterFacts => ({
  title: '', kind: 'prose', isFolder: false, values: {}, presence: new Map(), words: 0, modifiedAt: new Date(DISTANT_PAST),
  includeInCompile: true, locked: false, synopsis: '', notesEmpty: true, comments: [], openSuggestions: 0, nodes: new Set(),
  ...o, subtreeWords: o.subtreeWords ?? o.words ?? 0,
});

/** `FilterContext`: what the rules need beyond one fragment's facts. */
export interface FilterContext {
  /** The project's fields (option names, which fields hold tags or a checkbox). */
  fields: readonly Pick<MetadataField, 'id' | 'type' | 'options'>[];
  now: Date;
  /** The fragments each text rule found in an index, by rule id. A rule without an entry tests the facts' own text,
   *  and doesn't narrow anything when they have none. */
  textMatches: ReadonlyMap<string, ReadonlySet<string>>;
}

export const filterContext = (o: Partial<FilterContext> = {}): FilterContext =>
  ({ fields: o.fields ?? [], now: o.now ?? new Date(), textMatches: o.textMatches ?? new Map() });

/** `trimmingCharacters(in: .whitespacesAndNewlines).isEmpty`. */
export function isBlank(s: string): boolean {
  for (const cp of scalars(s)) if (!isCFWhitespaceOrNewline(cp)) return false;
  return true;
}

/** `Calendar.current.date(byAdding: .day, value: -days, to: now)`: calendar days in the runtime's zone. */
function daysBefore(now: Date, days: number): Date {
  const d = new Date(now.getTime());
  d.setDate(d.getDate() - days);
  return d;
}

// MARK: Matching

/** `Rule.matches`. */
export function ruleMatches(rule: SavedSearchRule, f: FilterFacts, context: FilterContext = filterContext()): boolean {
  let result: boolean;
  const value = ruleValue(rule);
  switch (rule.kind) {
    case 'field':
      if (rule.field === undefined) return true;
      result = fieldMatches(rule, rule.field, f, context);
      break;
    case 'text': case 'notes': {
      if (rule.kind === 'notes' && rule.op === 'empty') { result = f.notesEmpty; break; }
      const query = new WordQuery(value ?? '');
      if (query.isEmpty) return true; // a rule not filled in yet doesn't narrow anything
      const found = context.textMatches.get(rule.id);
      if (found) result = found.has(f.id);
      else if (f.text) result = query.matches(rule.kind === 'text' ? f.text.body : f.text.notes);
      else return true;
      break;
    }
    case 'title': case 'synopsis': {
      if (rule.kind === 'synopsis' && rule.op === 'empty') { result = isBlank(f.synopsis); break; }
      const query = new WordQuery(value ?? '');
      if (query.isEmpty) return true;
      result = query.matches(rule.kind === 'title' ? f.title : f.synopsis);
      break;
    }
    case 'entity': {
      const wanted = rule.values.filter((v) => v !== '');
      if (!wanted.length) return true;
      const least = Math.max(presenceRank(rule.level ?? 'mentioned'), presenceRank('mentioned'));
      const present = (e: string) => presenceRank(f.presence.get(e) ?? 'absent') >= least;
      result = rule.all ? wanted.every(present) : wanted.some(present);
      break;
    }
    case 'modified-within':
      result = f.modifiedAt.getTime() >= daysBefore(context.now, Math.max(0, rule.days ?? 30)).getTime();
      break;
    case 'included-in-compile':
      result = f.includeInCompile;
      break;
    case 'words': {
      const n = rule.number ?? 0;
      switch (rule.op ?? 'lt') {
        case 'gt': result = f.words > n; break;
        case 'between': result = f.words >= n && f.words <= (rule.upper ?? n); break;
        default: result = f.words < n;
      }
      break;
    }
    case 'target': {
      const target = WordTarget.value(f.values);
      if (target === null) { result = false; break; }
      result = value === 'reached' ? f.subtreeWords >= target : f.subtreeWords < target;
      break;
    }
    case 'locked':
      result = f.locked;
      break;
    case 'kind': {
      // Fragment kinds ("prose", "note"…) and shapes ("document", "folder"): each named must fit.
      const shapes = rule.values.filter((v) => v === 'document' || v === 'folder');
      const kinds = rule.values.filter((v) => v !== 'document' && v !== 'folder');
      if (!rule.values.length) return true;
      result = (!kinds.length || kinds.includes(f.kind)) && (!shapes.length || shapes.includes(f.isFolder ? 'folder' : 'document'));
      break;
    }
    case 'within':
      if (!rule.values.length) return true;
      result = rule.values.some((v) => f.nodes.has(v));
      break;
    case 'comments':
      result = f.comments.some((c) => {
        const categoryFits = rule.category === undefined ? true : rule.category === 'none' ? c.category === null : c.category === rule.category;
        return categoryFits && (rule.author === undefined || c.author === rule.author);
      });
      break;
    case 'suggestions':
      result = f.openSuggestions > 0;
      break;
    case 'unsupported':
      return false;
  }
  return rule.negate ? !result : result;
}

function fieldMatches(rule: SavedSearchRule, id: string, f: FilterFacts, context: FilterContext): boolean {
  const definition = context.fields.find((x) => x.id === id);
  const stored = f.values[id];
  const shown = definition ? fieldDisplay(definition, stored) : stored ? displayString(stored) : '';
  // An option the writer took out, or blank text, is no value.
  const isEmpty = trimCFWhitespace(shown) === '';
  if (rule.op === 'empty') return isEmpty;
  if (rule.op === 'contains') {
    const query = new WordQuery(ruleValue(rule) ?? '');
    return query.isEmpty || query.matches(shown);
  }
  const wanted = rule.values.map(trimCFWhitespace);
  if (!wanted.length) return isEmpty;
  if (definition?.type === 'tags') {
    // A tags field: "is" a tag when the document has it; "is" nothing when it has none.
    const tags = stored?.type === 'text' ? parseTags(stored.value) : [];
    const has = (tag: string) => (tag === '' ? tags.length === 0 : hasTag(tags, tag));
    return rule.all ? wanted.every(has) : wanted.some(has);
  }
  if (definition?.type === 'boolean') {
    const on = stored?.type === 'boolean' ? stored.value : false;
    return wanted.some((w) => (['true', 'yes'].includes(w.toLowerCase()) ? on : !on));
  }
  const raw = stored?.type === 'text' ? stored.value : stored ? displayString(stored) : '';
  return wanted.some((want) => (want === '' ? isEmpty : !isEmpty && (raw === want || shown.toLowerCase() === want.toLowerCase())));
}

// MARK: Words

/** `folding(options: [.caseInsensitive, .diacriticInsensitive])`, as the MCP search folds. */
const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().normalize('NFC');
const WORD_CHARACTER = /^[\p{Alphabetic}\p{N}]/u;

/** `WordQuery`: the words a text rule looks for — each a word prefix, or a quoted phrase (consecutive words, the last a
 *  prefix). Case and accents are ignored and anything but letters and digits separates words, as the index's
 *  unicode61 tokenizer does, so testing open text and asking an index agree. */
export class WordQuery {
  readonly terms: string[][];

  constructor(text: string) {
    const terms: string[][] = [];
    text.split('"').forEach((part, i) => {
      const words = WordQuery.words(part);
      if (i % 2 === 1) { if (words.length) terms.push(words); } else terms.push(...words.map((w) => [w]));
    });
    this.terms = terms;
  }

  get isEmpty(): boolean { return this.terms.length === 0; }

  /** Words as the index sees them: folded, split at anything that isn't a letter or digit (by Character, as Swift's
   *  `isLetter || isNumber` reads the first scalar). */
  static words(text: string): string[] {
    const out: string[] = [];
    let word = '';
    for (const c of graphemes(fold(text))) {
      if (WORD_CHARACTER.test(c)) word += c;
      else if (word) { out.push(word); word = ''; }
    }
    if (word) out.push(word);
    return out;
  }

  matches(text: string): boolean {
    return !this.terms.length || this.matchesWords(WordQuery.words(text));
  }

  /** `matches` over words already split by `WordQuery.words` (the browser keeps each document's, so typing in the
   *  filter doesn't split the whole book again at every key). */
  matchesWords(tokens: readonly string[]): boolean {
    if (!this.terms.length) return true;
    return this.terms.every((term) => {
      for (let start = 0; start + term.length <= tokens.length; start++) {
        if (term.every((w, i) => (i === term.length - 1 ? tokens[start + i].startsWith(w) : tokens[start + i] === w))) return true;
      }
      return false;
    });
  }

  /** The FTS5 query for these words in one column (`body`, `notes`, …). */
  ftsPattern(column: string): string | null {
    if (!this.terms.length) return null;
    return `${column} : (` + this.terms.map((t) => `"${t.join(' ')}"*`).join(' AND ') + ')';
  }
}

/** `Rule.isTextRule`: whether the rule reads the body or notes (through an index, or the facts' text). Titles, synopses
 *  and fields are in the facts. */
export function isTextRule(rule: SavedSearchRule): boolean {
  switch (rule.kind) {
    case 'text': return !new WordQuery(ruleValue(rule) ?? '').isEmpty;
    case 'notes': return rule.op !== 'empty' && !new WordQuery(ruleValue(rule) ?? '').isEmpty;
    default: return false;
  }
}

/** `Rule.textColumn`: the index column a text rule searches. */
export const textColumn = (rule: SavedSearchRule) => (rule.kind === 'notes' ? 'notes' : 'body');

// MARK: The tree a filter shows

/** `FilterResult`: a filtered arrangement — the nodes that match, drawn as usual, and the folders above them, drawn
 *  faintly so the matches keep their place in the book. */
export interface FilterResult {
  matches: Set<string>;
  context: Set<string>;
  /** Nodes with a match under them: the folders that open themselves. */
  opened: Set<string>;
  /** Matching documents (not folders), and every document in the arrangement. */
  documents: number;
  total: number;
  /** The matching nodes' own words. */
  words: number;
}

export const FilterResult = {
  /** `FilterResult.make`: runs `matching` on every node with a fragment. */
  make(arrangement: Arrangement, matching: (fragmentID: string) => boolean, words: (fragmentID: string) => number): FilterResult {
    const result: FilterResult = { matches: new Set(), context: new Set(), opened: new Set(), documents: 0, total: 0, words: 0 };
    // Whether anything at or under the nodes matched.
    const walk = (nodes: ArrangementTreeNode[]): boolean => {
      let any = false;
      for (const n of nodes) {
        const below = walk(n.children);
        if (below) result.opened.add(n.node.id);
        const f = n.node.fragmentID;
        const isDocument = f !== undefined && !n.node.isFolder;
        if (isDocument) result.total += 1;
        if (f !== undefined && matching(f)) {
          result.matches.add(n.node.id);
          result.words += words(f);
          if (isDocument) result.documents += 1;
          any = true;
        } else if (below) {
          result.context.add(n.node.id);
        }
        any = any || below;
      }
      return any;
    };
    walk(buildTree(arrangement));
    return result;
  },

  /** `shows`: whether the node shows at all (a match, or a folder above one). */
  shows: (result: FilterResult, nodeID: string) => result.matches.has(nodeID) || result.context.has(nodeID),

  /** `orderedMatches(in:)`: the matching nodes in reading order (the List mode, and Write's filtered scrivenings). */
  orderedMatches: (result: FilterResult, arrangement: Arrangement): ArrangementNode[] =>
    flattened(arrangement).map((x) => x.node).filter((n) => result.matches.has(n.id)),
};

// MARK: Facts from what the project holds

/** What a document gives the facts, read once: everything but where it sits (`nodes`, `subtreeWords`, `isFolder`). */
export function ownFacts(f: FragmentRecord, o: {
  /** The project's tags fields (their values read as every device's tags). */
  tagFields: Iterable<string>;
  presence?: Map<string, PresenceLevel>;
  locked?: boolean;
  words?: number;
  /** The plain body and the notes, for text rules without an index. */
  text?: boolean;
}): FilterFacts {
  const values = { ...f.metadata };
  for (const field of o.tagFields) if (values[field] !== undefined) values[field] = { type: 'text', value: joinTags(fragmentTags(f, field)) };
  const words = o.words ?? wordCount(f.text, f.kind);
  return filterFacts({
    id: f.id, title: f.title, kind: f.kind, values, presence: o.presence ?? new Map(), words, subtreeWords: words,
    modifiedAt: f.modifiedAt ?? new Date(DISTANT_PAST), includeInCompile: f.includeInCompile, locked: o.locked ?? false,
    synopsis: f.synopsis, notesEmpty: isBlank(f.notes),
    comments: f.comments.filter((c) => c.parentId === null && c.status === 'open').map((c) => ({ category: c.category, author: c.authorKind })),
    openSuggestions: f.suggestions.filter((s) => s.status === 'open').length,
    ...(o.text ? { text: { body: indexedBody(f), notes: f.notes } } : {}),
  });
}

/** What the index keeps as a document's body: a script's printed text, a map's notes and labels (`mapPlainText`; its
 *  `text` is unused), else the prose's plain text. */
export function indexedBody(f: Pick<FragmentRecord, 'kind' | 'text' | 'map'>): string {
  if (f.kind === 'screenplay') return fountainPlainText(f.text);
  if (f.kind === 'map') return f.map ? mapPlainText(f.map) : '';
  return plainText(f.text);
}

/** Presence rows as each fragment's entities at their highest level (absent left out). */
export function presenceLevels(rows: Iterable<Pick<PresenceRow, 'fragmentID' | 'entityID' | 'level'>>): Map<string, Map<string, PresenceLevel>> {
  const out = new Map<string, Map<string, PresenceLevel>>();
  for (const p of rows) {
    if (p.level === 'absent') continue;
    let levels = out.get(p.fragmentID);
    if (!levels) out.set(p.fragmentID, (levels = new Map()));
    const was = levels.get(p.entityID);
    if (was === undefined || presenceRank(p.level) > presenceRank(was)) levels.set(p.entityID, p.level);
  }
  return out;
}

/**
 * `FilterFacts.live`: facts for every fragment in an arrangement, from what the project view holds — the fragments'
 * records, presence rows, effective locks and live word counts. Text rules on the body and notes need
 * `FilterContext.textMatches`, or `text: true` (the plain text of each, which the browser can afford where the Mac asks
 * its index).
 */
export function liveFacts(arrangement: Arrangement, o: {
  fragment: (id: string) => FragmentRecord | null | undefined;
  fields: readonly Pick<MetadataField, 'id' | 'type'>[];
  presence: Iterable<Pick<PresenceRow, 'fragmentID' | 'entityID' | 'level'>>;
  locks: ReadonlySet<string>;
  words?: ReadonlyMap<string, number> | ((id: string) => number | undefined);
  text?: boolean;
}): Map<string, FilterFacts> {
  const levels = presenceLevels(o.presence);
  const tagFields = o.fields.filter((x) => x.type === 'tags').map((x) => x.id);
  const wordsOf = typeof o.words === 'function' ? o.words : (id: string) => (o.words as ReadonlyMap<string, number> | undefined)?.get(id);
  const own = new Map<string, number>();
  const ownWords = (id: string) => {
    let n = own.get(id);
    if (n === undefined) {
      const record = o.fragment(id);
      n = wordsOf(id) ?? (record ? wordCount(record.text, record.kind) : 0);
      own.set(id, n);
    }
    return n;
  };
  const facts = new Map<string, FilterFacts>();
  const walk = (nodes: ArrangementTreeNode[], above: Set<string>): number => {
    let sum = 0;
    for (const n of nodes) {
      const path = new Set(above).add(n.node.id);
      const below = walk(n.children, path);
      const f = n.node.fragmentID;
      if (f === undefined) { sum += below; continue; }
      const words = ownWords(f);
      sum += words + below;
      const existing = facts.get(f);
      if (existing) {
        for (const id of path) existing.nodes.add(id);
        continue;
      }
      const record = o.fragment(f);
      const base = record
        ? ownFacts(record, { tagFields, presence: levels.get(f), locked: o.locks.has(f), words, text: o.text })
        : filterFacts({ id: f, presence: levels.get(f) ?? new Map(), locked: o.locks.has(f), words });
      facts.set(f, { ...base, isFolder: n.node.isFolder || n.children.length > 0, subtreeWords: words + below, nodes: path });
    }
    return sum;
  };
  walk(buildTree(arrangement), new Set());
  return facts;
}

// MARK: Words for a rule

const KIND_NAMES: Record<string, string> = {
  document: 'documents', folder: 'folders', prose: 'scenes', screenplay: 'script scenes', note: 'notes', research: 'research',
  'front-matter': 'book matter',
};

/** `Rule.describe`: the rule in plain English ("Status: Draft or Revised", "POV: Hyde", "not changed in the last 30
 *  days"), for the CLI and agents. `title` names a node or fragment (for "within"). */
export function describeRule(rule: SavedSearchRule, fields: readonly Pick<MetadataField, 'id' | 'name' | 'type' | 'options'>[],
  entities: readonly Pick<StoryEntity, 'id' | 'name'>[], title: (id: string) => string | null | undefined = () => null): string {
  const not = rule.negate ? 'not ' : '';
  const joiner = rule.all ? ' and ' : ' or ';
  const value = ruleValue(rule);
  const entityName = (id: string) => entities.find((e) => e.id === id)?.name ?? id;
  switch (rule.kind) {
    case 'field': {
      const field = fields.find((x) => x.id === rule.field);
      const name = field?.name ?? rule.field ?? 'Field';
      if (rule.op === 'empty' || !rule.values.length || (rule.values.length === 1 && rule.values[0] === '')) return `${name} is ${not}empty`;
      if (rule.op === 'contains') return `${name} ${rule.negate ? 'doesn’t contain' : 'contains'} “${value ?? ''}”`;
      const shown = rule.values.map((v) => {
        if (v === '') return 'none';
        const option = field?.options.find((x) => x.id === v);
        if (option) return option.name;
        if (field?.type === 'entity-ref') return entityName(v);
        if (field?.type === 'boolean') return ['true', 'yes'].includes(v.toLowerCase()) ? 'yes' : 'no';
        return v;
      });
      return `${name}${rule.negate ? ' isn’t' : ':'} ${shown.join(joiner)}`;
    }
    case 'text': case 'title': case 'synopsis': case 'notes': {
      const what = { text: 'Text', title: 'Title', synopsis: 'Synopsis', notes: 'Notes' }[rule.kind];
      if (rule.op === 'empty') return `${what} is ${not}empty`;
      return `${what} ${rule.negate ? 'doesn’t contain' : 'contains'} “${value ?? ''}”`;
    }
    case 'entity': {
      const names = rule.values.map(entityName).join(joiner);
      const at = rule.level === 'pov' ? ' as POV' : rule.level === 'on-stage' ? ' on stage' : '';
      return `${names} ${rule.negate ? 'not in it' : 'in it'}${at}`;
    }
    case 'modified-within': return `${rule.negate ? 'not changed' : 'changed'} in the last ${rule.days ?? 30} days`;
    case 'included-in-compile': return `${not}in the compile`;
    case 'words': {
      const n = Math.trunc(rule.number ?? 0);
      switch (rule.op ?? 'lt') {
        case 'gt': return `${not}over ${n} words`;
        case 'between': return `${not}${n}–${Math.trunc(rule.upper ?? rule.number ?? 0)} words`;
        default: return `${not}under ${n} words`;
      }
    }
    case 'target': return value === 'reached' ? `${not}reached its target` : `${not}under its target`;
    case 'locked': return rule.negate ? 'unlocked' : 'locked';
    case 'kind': return `${not}${rule.values.map((v) => KIND_NAMES[v] ?? v).join(joiner)}`;
    case 'within': return `${rule.negate ? 'not within' : 'within'} ${rule.values.map((v) => title(v) ?? v).join(joiner)}`;
    case 'comments': {
      let words = rule.negate ? 'no open comments' : 'open comments';
      if (rule.category !== undefined) words += rule.category === 'none' ? ' without a category' : ' in a category';
      if (rule.author !== undefined) words += rule.author === 'agent' ? ' from agents' : ' from people';
      return words;
    }
    case 'suggestions': return rule.negate ? 'no open suggestions' : 'open suggestions';
    case 'unsupported': return 'a rule made in a newer version';
  }
}
